/**
 * Where to send someone after the login, when the page that sent them there
 * was left by a full page load (and so React Router's `state.from` is gone).
 *
 * 2026-10-21: every redirect from AuthContext reloads to /firebase-login, and
 * the login then landed the person on the default home (`hadFrom: false` in
 * the auth journal) instead of the page they were working on.
 *
 * Kept in sessionStorage: per tab, never sent anywhere, gone with the tab.
 * Only same-origin absolute paths are ever returned.
 *
 * 2026-10-22: a tab inside a synced Safari Tab Group was reloaded straight
 * into /firebase-login by the browser itself, with a live session and no code
 * of ours asking (auth journal: `pagehide` on /admin/conversations, then
 * `boot` on /firebase-login, nothing between). No redirect ran, so nothing
 * was remembered and the person landed on the home page. The path is now
 * remembered on every route change, so any reload into the login — ours or
 * not — returns to the page the tab was on.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @ts-ignore
import { isPublicRoute, skipsAuthGateway } from './publicRoutes';

const KEY = 'grid:login_return_to';

function isSafePath(path: unknown): path is string {
  return typeof path === 'string'
    && path.startsWith('/')
    && !path.startsWith('//')
    && !path.startsWith('/\\')
    && !path.startsWith('/firebase-login')
    && !path.startsWith('/login')
    && !/[\u0000-\u001f\u007f\\]/.test(path);
}

export function rememberReturnPath(): void {
  try {
    const { pathname, search, hash } = window.location;
    const path = `${pathname}${search}${hash}`;
    if (isSafePath(path)) sessionStorage.setItem(KEY, path);
  } catch {
    /* ignore */
  }
}

/** Read without consuming: the login may compute its target more than once
 * before it navigates. Call forgetReturnPath() once it does. */
export function peekReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(KEY);
    return isSafePath(path) ? path : null;
  } catch {
    return null;
  }
}

export function forgetReturnPath(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** Mounted once inside the router: remembers every page the tab shows. */
export function ReturnPathTracker(): null {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    // Public pages carry their own authorization (a token in the path) and
    // are never where a signed-in person should be sent back to.
    if (isPublicRoute(pathname) || skipsAuthGateway(pathname)) return;
    rememberReturnPath();
  }, [pathname, search, hash]);
  return null;
}
